import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import type { ConversationAgentPreference } from 'lib-shared-types-api';
import moment from 'moment-timezone';
import { useMemo } from 'react';

import { ConversationTimestamp } from 'components/conversation/components/conversationTimestamp/conversationTimestamp';
import { getUserDisplayName } from 'utils/user';

import { useConversationRow } from '../../../../../utils/useConversationRow';

import styles from './conversationsTableStatusAgentPreference.module.scss';

export const ConversationsTableStatusAgentPreference =
  (): JSX.Element | null => {
    const {
      conversationAndRequester: { agent_preference },
    } = useConversationRow();

    const tooltip = useMemo((): JSX.Element | null => {
      // The backend may still return the `agent_preference`, even when the `valid_until` is expired, so
      // we need to check ourselves if the date is in the future and only show the helper if it is.
      const hasValidAgentPreference =
        agent_preference && moment(agent_preference.valid_until).isAfter();

      if (!hasValidAgentPreference) return null;

      const agent = <b>{getUserDisplayName(agent_preference.agent)}</b>;

      const validUntil = (
        <ConversationTimestamp
          time={agent_preference.valid_until}
          isSecondsDisplayed={false}
          textProps={{
            as: 'span',
            variant: '2',
            subdued: false,
            color: 'inherit',
          }}
        />
      );

      const reason = (
        <b>
          {prettyAgentPreferenceType[agent_preference.agent_preference_type]}
        </b>
      );

      return (
        <Tooltip
          head="Agent preference"
          content={
            <>
              Conversation is reserved for {agent} until {validUntil} with
              reason: {reason}
            </>
          }
          className={styles.tooltip}
        >
          <Icon name="info" size={20} color={typography.color.secondary} />
        </Tooltip>
      );
    }, [agent_preference]);

    return tooltip;
  };

const prettyAgentPreferenceType: Record<
  ConversationAgentPreference['agent_preference_type'],
  string
> = {
  last_agent: 'last agent',
  preferred_agent: 'preferred agent',
  follow_up: 'follow-up',
};
